// vacuum/shell.jsx — full-takeover spot-clean surface.
//
// House-wide, not room-scoped: opens from the drawer, and from a card on
// the four room tiles that have any vacuum coverage (Office, Master
// Bedroom, Master Ensuite, Pavilion). The whole-house and scheduled runs already exist as
// automations in ha-vacuum — this surface is deliberately only for "clean
// these rooms, now".
//
// Contract: HANDOFF-ha-vacuum.md (symlink → ../ha-vacuum/HANDOFF.md).
// Policy lives in policy.js; the dispatch sequence in dispatch.js.

import * as React from 'react';
import { TakeoverShell, Card, A_FONT, fs, BusyDot, Segmented } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import {
  VACUUM, AREAS, MODES, modeById, dispatchButton, preRollSeconds,
  isRunningSegment, canDispatch, isAway, dndState, nowMinutes,
} from './policy.js';
import { subscribeJob, startSpotClean, abortJob } from './dispatch.js';

export function VacuumShell({ T, open, onClose, hass, viewport, initialAreas }) {
  const [selected, setSelected] = React.useState(() => new Set());
  const [modeId, setModeId] = React.useState('vacuum');
  const [job, setJob] = React.useState(null);
  const [, setTick] = React.useState(0);

  React.useEffect(() => subscribeJob(setJob), []);

  // Re-render once a second only while a job is in flight, so the
  // pre-roll countdown and the elapsed timer move. Nothing else on this
  // surface needs a clock.
  React.useEffect(() => {
    if (!job) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [!!job]);

  // Seed the selection when the sheet opens. A room card passes its
  // areas; the drawer passes nothing and leaves the last selection be.
  const seed = initialAreas ? initialAreas.join(',') : '';
  React.useEffect(() => {
    if (!open || !initialAreas || !initialAreas.length) return;
    setSelected(new Set(initialAreas));
  }, [open, seed]);

  if (!open) return null;

  const st = hass?.states || {};
  const vacState    = st[VACUUM.entity]?.state;
  const status      = st[VACUUM.status]?.state;
  const battery     = st[VACUUM.battery]?.state;
  const currentRoom = st[VACUUM.currentRoom]?.state;
  const errorState  = st[VACUUM.error]?.state;
  const waterShort  = st[VACUUM.waterShort]?.state === 'on';
  const mopOn       = st[VACUUM.mopAttached]?.state === 'on';
  const dndOn       = st[VACUUM.dndSwitch]?.state === 'on';
  const dnd = dndOn
    ? dndState(nowMinutes(new Date()), st[VACUUM.dndBegin]?.state, st[VACUUM.dndEnd]?.state)
    : 'clear';

  const mode = modeById(modeId);
  const running = isRunningSegment(status);
  const away = isAway(vacState);
  const ready = canDispatch(vacState);

  // busy / lit / disabled come from policy.js so the three-way split is
  // unit-tested rather than eyeballed. Busy is LIT — see PowerButton.
  const btn = dispatchButton({ ready, count: selected.size, job });
  const preRoll = preRollSeconds(job, Date.now());
  const btnLabel = preRoll != null ? `${btn.label} ${preRoll}s` : btn.label;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const warnings = [];
  if (errorState && errorState !== 'none' && errorState !== 'unknown') {
    warnings.push(`Needs a human: ${errorState.replace(/_/g, ' ')}`);
  }
  if (waterShort && mode.mop !== 'off') warnings.push('Water tank is empty.');
  if (!mopOn && mode.mop !== 'off') warnings.push('No mop pad fitted — this mode will not mop.');
  if (dnd === 'inside') {
    warnings.push('Do Not Disturb is on. A run needing a recharge will lose the rest of it.');
  } else if (dnd === 'approaching') {
    warnings.push('Do Not Disturb starts within the hour. A long run may not finish.');
  }

  return (
    <TakeoverShell T={T} viewport={viewport}
      eyebrow="Housekeeping" title="Vacuum" onClose={onClose}>

      <Hero T={T} job={job} running={running} status={status} vacState={vacState}
        battery={battery} currentRoom={currentRoom}/>

      {warnings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
          {warnings.map((w) => (
            <div key={w} style={{
              fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
              color: T.attention, textAlign: 'center',
            }}>{w}</div>
          ))}
        </div>
      )}

      <Card T={T} eyebrow="Mode" style={{ marginTop: 18 }}>
        <Segmented T={T} ariaLabel="Mode" height={46}
          options={MODES.map((m) => ({ value: m.id, label: m.label }))}
          value={modeId} onChange={setModeId}/>
        <p style={{ margin: 0, color: T.inkSoft, fontSize: fs(T, 13), lineHeight: 1.5 }}>
          {mode.hint}
        </p>
      </Card>

      <Card T={T} style={{ marginTop: 14 }}
        eyebrow={selected.size ? `Rooms · ${selected.size} selected` : 'Rooms'}
        headerAction={
          <button onClick={() => setSelected(selected.size ? new Set() : new Set(AREAS.map((a) => a.id)))}
            style={{
              height: 44, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer',
              color: T.inkSoft, fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
            }}>{selected.size ? 'Clear' : 'All'}</button>
        }>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {AREAS.map((a, i) => (
            <RoomRow key={a.id} T={T} area={a} first={i === 0}
              checked={selected.has(a.id)}
              here={running && currentRoom === a.name}
              onClick={() => toggle(a.id)}/>
          ))}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 10, marginTop: 18, alignItems: 'stretch' }}>
        <button onClick={() => !btn.disabled && startSpotClean(hass, [...selected], modeId)}
          disabled={btn.disabled}
          aria-busy={btn.busy || undefined}
          style={{
            flex: 1, padding: '16px 20px', borderRadius: RADIUS.control,
            // Three states, and the busy one is the house idiom rather
            // than anything invented here: the press lifts out as a key
            // with a BusyDot and its wording, as the Activity verbs and
            // the shade buttons do, so a press here looks like a press
            // anywhere else. Ready is the solid primary fill (it is the
            // primary action); only genuinely unavailable goes flat.
            border: 'none',
            background: btn.busy ? T.keyFill : btn.lit ? T.primary : T.cardSoft,
            boxShadow: btn.busy ? T.keyShadow : 'none',
            color: btn.busy ? T.ink : btn.lit ? T.primaryInk : T.mute,
            fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: 600,
            cursor: btn.disabled ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'background .2s, color .2s, box-shadow .2s',
          }}>
          {btn.busy && <BusyDot T={T} size={16}/>}
          {btnLabel}
        </button>

        {(away || job) && (
          // Two jobs in one control, and which one it is depends on where
          // the robot is. During the ten second pre-roll it has not moved,
          // so this is purely "cancel the pending dispatch" — calling
          // return_to_base at a docked robot would be a no-op dressed up
          // as an action. Once it is out, cancelling the (already
          // finished) dispatch is not enough on its own and it has to be
          // recalled as well. Both paths abort first, so a recall can
          // never leave a clean queued behind it.
          <button onClick={() => {
              abortJob();
              if (away) hass?.callService?.('vacuum', 'return_to_base', {}, { entity_id: VACUUM.entity });
            }}
            style={{
              padding: '16px 20px', borderRadius: RADIUS.control,
              border: 'none', background: T.cardSoft,
              color: T.ink, fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: 500,
              cursor: 'pointer',
            }}>{away ? 'Send home' : 'Cancel'}</button>
        )}
      </div>

      <p style={{
        margin: '14px 0 0', color: T.inkSoft, fontSize: fs(T, 13),
        lineHeight: 1.5, textAlign: 'center',
      }}>
        {ready
          ? 'The robot waits about ten seconds after you press, while the mode reaches it.'
          : 'Available when the robot is docked.'}
      </p>
    </TakeoverShell>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────
//
// No ETA, deliberately. One whole-house measurement exists (~3 h vacuum,
// ~2 h mop) and no per-room timings at all, so any estimate here would be
// invented. Elapsed time is honest and nearly as useful.

function Hero({ T, job, running, status, vacState, battery, currentRoom }) {
  let line = 'Docked';
  let sub = null;

  if (job) {
    line = 'Starting a clean';
    sub = `${job.areas.length} room${job.areas.length === 1 ? '' : 's'} · ${modeById(job.mode).label.toLowerCase()}`;
  } else if (running) {
    const mopping = status.includes('mop');
    line = currentRoom && currentRoom !== 'unknown'
      ? `${mopping ? 'Mopping' : 'Vacuuming'} the ${currentRoom}`
      : (mopping ? 'Mopping' : 'Vacuuming');
    sub = 'Tap Send home to recall it';
  } else if (vacState && vacState !== 'docked') {
    line = prettyStatus(status) || prettyStatus(vacState) || 'Away from the dock';
  } else if (battery != null && battery !== 'unknown') {
    sub = `Battery ${battery}%`;
  }

  return (
    <div style={{ textAlign: 'center', marginTop: 6 }}>
      <div style={{
        fontFamily: A_FONT.serif, fontSize: 36, color: T.ink,
        lineHeight: 1.1, fontWeight: 500,
      }}>{line}</div>
      {sub && (
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), color: T.inkSoft, marginTop: 8,
        }}>{sub}</div>
      )}
    </div>
  );
}

function prettyStatus(s) {
  if (!s || s === 'unknown') return null;
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// ── Room row ─────────────────────────────────────────────────────────

function RoomRow({ T, area, checked, here, first, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={checked}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        minHeight: 52, padding: '0 2px', background: 'transparent',
        border: 'none', borderTop: first ? 'none' : `1px solid ${T.hair}`,
        cursor: 'pointer', textAlign: 'left', width: '100%',
      }}>
      <span style={{
        width: 26, height: 26, borderRadius: 8, flexShrink: 0,
        background: checked ? T.selected : T.card,
        boxShadow: checked ? 'none' : `inset 0 0 0 1.5px ${T.hairHi}`,
        color: T.selectedInk, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l5 5L20 7"/>
          </svg>
        )}
      </span>
      <span style={{
        flex: 1, fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: 500,
        color: checked ? T.ink : T.inkSoft,
      }}>{area.name}</span>
      {here && (
        <span style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 12), fontWeight: 600,
          color: T.paper, background: T.live, padding: '3px 9px', borderRadius: 8,
        }}>Here</span>
      )}
    </button>
  );
}
